// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import TextField from "@awc/MobiusOverrides/TextField";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ChangePasswordActions from "@insite/content-library/Widgets/ChangePassword/ChangePasswordActions";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden/Hidden";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    validatePassword,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    pageTitle: getCurrentPage(state).fields.title,
    // XNG-Changes
    isTabletScreen: (state as CustomAppState).xCommon?.isTabletScreen,
});

export interface ChangePasswordStyles {
    actions: HiddenProps;
    passwordRequirementsGridItem: GridItemProps;
    passwordRequirementsGridContainer: GridContainerProps;
    passwordGridContainer: GridContainerProps;
    signOutWarningText: TypographyPresentationProps;
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    showPasswordsGridItem?: GridItemProps;
    showPasswordsCheckbox?: CheckboxPresentationProps;
    changePasswordLink?: LinkPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    // XNG-Change
    userInformationTextGridItem?: GridItemProps;
    passwordGridItemContainer?: GridContainerProps;
    actionsGridItem?: GridItemProps;
    userInformationText?: TypographyProps;
    textFieldStyle?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordReqGridItem?: GridItemProps;
}

export const changePasswordStyles: ChangePasswordStyles = {
    actions: {
        css: css`
            width: 100%;
        `,
    },
    passwordGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        max-width: 300px;
                    `,
                    css`
                        max-width: 300px;
                    `,
                    css`
                        max-width: 300px;
                    `,
                ])}
        `,
        gap: 10,
    },
    passwordRequirementsGridContainer: {
        gap: 2,
        css: css`
            margin-bottom: 10px;
        `,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    showPasswordsGridItem: {
        width: 12,
    },
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    signOutWarningText: {
        css: css`
            padding-bottom: 10px;
            display: block;
        `,
    },
    userInformationTitle: {
        variant: "h4",
        as: "h2",
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
};

const styles = changePasswordStyles;

const ChangePasswordView = ({ accountSettings, pageTitle, validatePassword, isTabletScreen }: Props) => {
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [error, setError] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);

    useEffect(() => {
        setError(
            !!newPasswordErrorMessage || !newPassword || !confirmNewPassword || confirmNewPassword !== newPassword,
        );
    }, [newPassword, newPasswordErrorMessage, confirmNewPassword]);

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        logOutUserAfterPasswordChange,
    } = accountSettings;

    const currentPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setPassword(event.currentTarget.value);
    };

    const newPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const newPassword = event.currentTarget.value;

        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setNewPasswordErrorMessage(errorMessage);
            },
        });

        setNewPassword(newPassword);
    };

    const confirmNewPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setConfirmNewPassword(event.currentTarget.value);
    };

    const confirmNewPasswordError =
        showValidation && newPassword && confirmNewPassword && newPassword !== confirmNewPassword
            ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
            : "";

    // XNG-Change: PasswordRequirementValidator
    return (
        <form>
            {/* <ChangePasswordHeader
                title={pageTitle}
                password={password}
                newPassword={newPassword}
                confirmNewPassword={confirmNewPassword}
                error={error}
                showValidation={showValidation}
                setShowValidation={setShowValidation}
            ></ChangePasswordHeader> */}
            {logOutUserAfterPasswordChange && (
                <Typography {...styles.signOutWarningText}>
                    {siteMessage("Account_Automatic_SignOut_Warning")}
                </Typography>
            )}
            <GridContainer {...styles.passwordGridContainer}>
                <GridItem {...styles.passwordGridItem}>
                    <GridContainer {...styles.passwordGridItemContainer}>
                        <GridItem {...styles.userInformationTextGridItem}>
                            <Typography {...styles.userInformationText}>
                                {translate("Enter your new password.")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.userInformationGridItem}>
                            <TextField
                                type={showPasswords ? "text" : "password"}
                                label={translate("Current Password")}
                                onInput={currentPasswordChangeHandler}
                                autoComplete="current-password"
                                // XNG-Changes
                                validateAs="password"
                                labelPosition={isTabletScreen ? "top" : "left"}
                                placeholder={translate("Enter current password")}
                                error={
                                    showValidation &&
                                    !password &&
                                    siteMessage("Field_Required").toString().replace("{0}", "Password")
                                }
                                {...styles.textFieldStyle}
                            />
                        </GridItem>
                        <GridItem {...styles.userInformationGridItem}>
                            <TextField
                                type={showPasswords ? "text" : "password"}
                                label={translate("New Password")}
                                onInput={newPasswordChangeHandler}
                                autoComplete="new-password"
                                // XNG-Changes
                                validateAs="password"
                                labelPosition={isTabletScreen ? "top" : "left"}
                                placeholder={translate("Enter new password")}
                                error={
                                    showValidation &&
                                    (newPassword
                                        ? newPasswordErrorMessage
                                        : siteMessage("Field_Required").toString().replace("{0}", "Password"))
                                }
                                {...styles.textFieldStyle}
                            />
                        </GridItem>
                        <GridItem {...styles.userInformationGridItem}>
                            <TextField
                                type={showPasswords ? "text" : "password"}
                                label={translate("Confirm New Password")}
                                onInput={confirmNewPasswordChangeHandler}
                                autoComplete="new-password"
                                // XNG-Changes
                                validateAs="password"
                                labelPosition={isTabletScreen ? "top" : "left"}
                                placeholder={translate("Confirm new password")}
                                error={
                                    showValidation &&
                                    (confirmNewPassword
                                        ? confirmNewPasswordError
                                        : siteMessage("Field_Required").toString().replace("{0}", "Password"))
                                }
                                {...styles.textFieldStyle}
                            />
                        </GridItem>
                        <GridItem {...styles.showPasswordsGridItem}>
                            <Checkbox
                                onChange={() => {
                                    setShowPasswords(!showPasswords);
                                }}
                                checked={showPasswords}
                            >
                                {translate("Show Passwords")}
                            </Checkbox>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.passwordReqGridItem}>
                    <GridContainer {...styles.passwordRequirementsGridContainer}>
                        <GridItem {...styles.passwordRequirementsGridItem}>
                            <Typography {...styles.userInformationTitle}>
                                {translate("Password Requirements")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.passwordRequirementsGridItem}>
                            {translate(
                                "Password must be at least {0} characters long",
                                passwordMinimumLength.toString(),
                            )}
                        </GridItem>
                        {passwordRequiresDigit && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {translate("Password must include at least one number")}
                            </GridItem>
                        )}
                        {passwordRequiresLowercase && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {translate("Password must include at least one lowercase character")}
                            </GridItem>
                        )}
                        {passwordRequiresUppercase && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {translate("Password must include at least one uppercase character")}
                            </GridItem>
                        )}
                        {passwordRequiresSpecialCharacter && (
                            <GridItem {...styles.passwordRequirementsGridItem}>
                                {translate("Password must include at least one non alphanumeric character")}
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.actionsGridItem}>
                    <ChangePasswordActions
                        password={password}
                        newPassword={newPassword}
                        confirmNewPassword={confirmNewPassword}
                        error={error}
                        showValidation={showValidation}
                        setShowValidation={setShowValidation}
                    ></ChangePasswordActions>
                </GridItem>
            </GridContainer>
        </form>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ChangePasswordView),
    definition: {
        allowedContexts: ["ChangePasswordPage"],
        group: "Change Password",
    },
};

export default widgetModule;
